import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { isAfter, isBefore } from 'date-fns/esm';
import { Text, Link } from '@bb/elements-web';
import errorReporter from '@app/errorReporter';

import { message } from 'components';
import { formatTimeStr, getLocalTimeZone } from 'utils';
import { buildItemCountText } from '../../../TourEditPage/utils';
import ShowingDetailsContext from '../../ShowingDetailsV2/ShowingDetailsContext';
import { TIME_CHANGE_STATUSES } from '../../constants';
import Section from '../Section';

const TourSection: React.FC = () => {
  const { fromTourPage, tourDetails, auth } = useContext(ShowingDetailsContext);
  const [startTime, setStartTime] = useState<Date>();
  const [endTime, setEndTime] = useState<Date>();
  const [tourItemCount, setTourItemCount] = useState<number>(0);
  useEffect(() => {
    // trimmed from the logic in DashboardTour component
    if (!tourDetails) return;
    let currentNode: string | undefined | null = 'firstItem';
    let startTimeTemp: Date | undefined;
    let endTimeTemp: Date | undefined;
    try {
      let itemCount = 0;
      while (currentNode) {
        itemCount += 1;

        // finding the start time of the tour based on start of the earliest appointment
        const timeUsed = TIME_CHANGE_STATUSES.includes(
          tourDetails[currentNode].status || '',
        )
          ? tourDetails[currentNode].originalTime
          : tourDetails[currentNode].time;

        const timeEndUsed = TIME_CHANGE_STATUSES.includes(
          tourDetails[currentNode].status || '',
        )
          ? tourDetails[currentNode].originalTimeEnd
          : tourDetails[currentNode].timeEnd;
        if (timeUsed) {
          if (startTimeTemp) {
            // @ts-expect-error: string -> Date
            startTimeTemp = isBefore(
              new Date(timeUsed),
              new Date(startTimeTemp),
            )
              ? timeUsed
              : startTimeTemp;
          } else {
            // @ts-expect-error: string -> Date
            startTimeTemp = timeUsed;
          }
        }

        // finding the end time of the tour based on end of the last appointment
        if (timeEndUsed) {
          if (endTimeTemp) {
            // @ts-expect-error: string -> Date
            endTimeTemp = isAfter(new Date(timeEndUsed), new Date(endTimeTemp))
              ? timeEndUsed
              : endTimeTemp;
          } else {
            // @ts-expect-error: string -> Date
            endTimeTemp = timeEndUsed;
          }
        }

        currentNode = tourDetails[currentNode].nextItem;
      }

      setTourItemCount(itemCount);

      if (itemCount < Object.keys(tourDetails).length) {
        const { appointmentTourId = 'N/A' } = tourDetails.firstItem;
        throw new Error(
          `The tour data is corrupted tourId: ${appointmentTourId}`,
        );
      }
    } catch (err) {
      let appointmentTourId = 'N/A';
      let tourName = 'N/A';

      if (tourDetails.firstItem) {
        ({ appointmentTourId, tourName } = tourDetails.firstItem);
      }

      errorReporter.error(
        `Error trying to load the tour ${appointmentTourId}: ${err}`,
      );
      message.error(`Error loading tour "${tourName}"`);
    }

    setStartTime(startTimeTemp);
    setEndTime(endTimeTemp);
  }, [tourDetails]);

  if (!tourDetails) return <></>;
  return (
    <Section>
      <StyledTourInfoP size="large" thickness="bold" dataCy="tourName">
        {tourDetails.firstItem.tourName}
      </StyledTourInfoP>
      <StyledTourInfoP dataCy="tourTime" size="large">
        {startTime && endTime
          ? `${formatTimeStr(
              startTime,
              getLocalTimeZone(auth),
            )} - ${formatTimeStr(endTime, getLocalTimeZone(auth))} `
          : `No times selected `}
      </StyledTourInfoP>
      <StyledTourInfoP dataCy="tourStopCount" size="large">
        {buildItemCountText(tourItemCount)}
      </StyledTourInfoP>
      {!fromTourPage && (
        <Link
          size="large"
          dataCy="tour-editTour-button"
          href={`/#/tours/${tourDetails.firstItem.appointmentTourId}`}
        >
          Edit tour
        </Link>
      )}
    </Section>
  );
};

export default TourSection;

const StyledTourInfoP = styled(Text)`
  margin-bottom: 10px;
  &&:first-of-type {
    margin-top: 5px;
  }
`;
